import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, X, Check } from 'lucide-react';
import './CustomSearchableSelect.css';

export interface CustomSearchableSelectOption {
    label: string;
    value: string | number;
    image?: string | null;
    subLabel?: string;
}

export interface CustomSearchableSelectProps {
    label?: string;
    options: CustomSearchableSelectOption[];
    value?: string | number | (string | number)[];
    onChange: (value: any) => void;
    onMultiChange?: (values: (string | number)[]) => void;
    isMulti?: boolean;
    error?: string;
    placeholder?: string;
    leftIcon?: React.ReactNode;
    helperText?: string;
    className?: string;
    disabled?: boolean;
    required?: boolean;
    hideAvatar?: boolean;
}

const CustomSearchableSelect: React.FC<CustomSearchableSelectProps> = ({
    label,
    options,
    value,
    onChange,
    onMultiChange,
    isMulti = false,
    error,
    placeholder = 'Select an option',
    leftIcon,
    helperText,
    className = '',
    disabled = false,
    required = false,
    hideAvatar = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const API_URL = import.meta.env.VITE_API_URL || '';

    // Multi-select vs Single-select values
    const selectedValues: (string | number)[] = isMulti
        ? Array.isArray(value)
            ? value
            : value !== undefined && value !== '' && value !== null
            ? [value]
            : []
        : [];

    const selectedOption = !isMulti ? options.find(opt => opt.value === value) : undefined;
    const selectedOptions = isMulti ? options.filter(opt => selectedValues.includes(opt.value)) : [];

    const filteredOptions = options.filter(opt =>
        opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (opt.subLabel && opt.subLabel.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (option: CustomSearchableSelectOption) => {
        if (isMulti) {
            let nextValues: (string | number)[];
            if (selectedValues.includes(option.value)) {
                nextValues = selectedValues.filter(v => v !== option.value);
            } else {
                nextValues = [...selectedValues, option.value];
            }
            onChange(nextValues);
            onMultiChange?.(nextValues);
        } else {
            onChange(option.value);
            setIsOpen(false);
            setSearchTerm('');
        }
    };

    const handleRemoveChip = (e: React.MouseEvent, optValue: string | number) => {
        e.stopPropagation();
        const nextValues = selectedValues.filter(v => v !== optValue);
        onChange(nextValues);
        onMultiChange?.(nextValues);
    };

    const getImageUrl = (url: string) => {
        if (!url) return null;
        if (url.startsWith('http')) return url;
        return `${API_URL}${url}`;
    };

    return (
        <div className={`custom-searchable-select-container ${className} ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
            {label && (
                <label className="custom-searchable-select-label">
                    {label} {required && <span style={{ color: 'var(--status-error, #ef4444)' }}>*</span>}
                </label>
            )}

            <div 
                className={`custom-searchable-select-wrapper ${isMulti ? 'is-multi' : ''} ${isOpen ? 'open' : ''} ${error ? 'error' : ''}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                {leftIcon && <div className="custom-searchable-select-left-icon">{leftIcon}</div>}
                
                <div className={`custom-searchable-select-display ${leftIcon ? 'has-icon' : ''}`}>
                    {isMulti ? (
                        selectedOptions.length > 0 ? (
                            <div className="custom-searchable-select-chips">
                                {selectedOptions.map(opt => (
                                    <span key={opt.value} className="select-chip">
                                        {opt.label}
                                        <span 
                                            className="select-chip-remove"
                                            onClick={(e) => handleRemoveChip(e, opt.value)}
                                            title="Remove"
                                        >
                                            <X size={12} />
                                        </span>
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <span className="placeholder">{placeholder}</span>
                        )
                    ) : selectedOption ? (
                        <div className="selected-option-content">
                            {selectedOption.image && (
                                <img 
                                    src={getImageUrl(selectedOption.image) || ''} 
                                    alt="" 
                                    className="option-thumbnail"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                />
                            )}
                            <span>{selectedOption.label}</span>
                        </div>
                    ) : (
                        <span className="placeholder">{placeholder}</span>
                    )}
                </div>

                <div className="custom-searchable-select-right-icons">
                    <ChevronDown size={18} className={`chevron-icon ${isOpen ? 'rotate' : ''}`} />
                </div>
            </div>

            {isOpen && (
                <div className="custom-searchable-select-dropdown">
                    <div className="search-box">
                        <Search size={16} />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                        />
                        {searchTerm && (
                            <X size={14} className="clear-search" onClick={(e) => {
                                e.stopPropagation();
                                setSearchTerm('');
                            }} />
                        )}
                    </div>

                    <ul className="options-list">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => {
                                const isSelected = isMulti
                                    ? selectedValues.includes(option.value)
                                    : option.value === value;

                                return (
                                    <li 
                                        key={option.value}
                                        className={`option-item ${isSelected ? 'selected' : ''}`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleSelect(option);
                                        }}
                                    >
                                        <div className="option-content">
                                            {isMulti && (
                                                <div className={`option-checkbox ${isSelected ? 'checked' : ''}`}>
                                                    {isSelected && <Check size={12} strokeWidth={3} />}
                                                </div>
                                            )}
                                            {!hideAvatar && (
                                                option.image ? (
                                                    <img 
                                                        src={getImageUrl(option.image) || ''} 
                                                        alt="" 
                                                        className="option-thumbnail"
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(option.label) + '&background=random';
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="option-avatar-fallback">
                                                        {option.label.charAt(0).toUpperCase()}
                                                    </div>
                                                )
                                            )}
                                            <div className="option-info">
                                                <span className="option-label">{option.label}</span>
                                                {option.subLabel && <span className="option-sublabel">{option.subLabel}</span>}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })
                        ) : (
                            <li className="no-options">No results found</li>
                        )}
                    </ul>
                </div>
            )}

            {error && <p className="custom-searchable-select-error-text">{error}</p>}
            {helperText && !error && <p className="custom-searchable-select-helper-text">{helperText}</p>}
        </div>
    );
};

export default CustomSearchableSelect;

